/* eslint-disable import/prefer-default-export */
import { assoc, concat, dissoc, has, pipe, prop, when } from 'ramda';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import { createCacheUpdateHandler } from 'utils/helpers/fetch';
import { createSingleAbortableQuery } from 'utils/helpers/abortPreviousRequest';

const getUrl = concat('supplier-authorizations/');

// Controller ref for cancelling previous supplier auth list requests
const supplierAuthorizationsListController = { current: null };

// this can be expanded in the future to remap other params as needed,
// but for now we just need to remap pageSize to results
const remapParams = when(
  has('pageSize'),
  pipe(params => assoc('results', prop('pageSize', params), params), dissoc('pageSize'))
);

// Important: this adds an isFiltered: true property to the body of all requests sent to bulk activate/deactivate endpoints.
// This is required for the backend to properly handle the request and return the expected data.
// The endpoint without this property will activate the legacy "all" logic which does not work with the current frontend implementation.
// Do not remove this without coordinating with backend development to ensure continued functionality.
const addIsFilteredTrueToBody = assoc('isFiltered', true);

const supplierAuthorizationsEndpoints = loadingManagementApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    bulkActivateSupplierAuthorizations: mutation({
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getSupplierAuthorizationsList',
        (draft, { authorizationIds }) => {
          authorizationIds?.forEach(authId => {
            const rowToUpdate = draft.supplierAuthorizations?.find(
              auth => auth.productAuthorizationId === authId
            );
            if (rowToUpdate) {
              rowToUpdate.active = true;
            }
          });
        },
        { invalidateOnError: ['SupplierAuthorizationList'] }
      ),
      query: body => ({
        body: addIsFilteredTrueToBody(body), // See important note about addIsFilteredTrueToBody function above
        method: 'POST',
        url: getUrl('activate-supplier-authorizations'),
      }),
    }),
    bulkDeactivateSupplierAuthorizations: mutation({
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getSupplierAuthorizationsList',
        (draft, { authorizationIds }) => {
          authorizationIds?.forEach(authId => {
            const rowToUpdate = draft.supplierAuthorizations?.find(
              auth => auth.productAuthorizationId === authId
            );
            if (rowToUpdate) {
              rowToUpdate.active = false;
            }
          });
        },
        { invalidateOnError: ['SupplierAuthorizationList'] }
      ),
      query: body => ({
        body: addIsFilteredTrueToBody(body), // See important note about addIsFilteredTrueToBody function above
        method: 'POST',
        url: getUrl('deactivate-supplier-authorizations'),
      }),
    }),
    getExportList: query({
      query: params => ({
        params,
        responseHandler: response => response.blob(),
        url: getUrl('export'),
      }),
    }),
    getFilterLocationsList: query({
      query: params => ({
        params,
        url: getUrl('get-filter-locations'),
      }),
    }),
    getSupplierAuthorizationsList: query({
      providesTags: ['SupplierAuthorizationList'],
      queryFn: createSingleAbortableQuery(
        supplierAuthorizationsListController,
        async (params, api, extraOptions, baseQuery, signal) => {
          const remappedParams = remapParams(params);
          return baseQuery(
            {
              body: remappedParams,
              method: 'POST',
              signal,
              url: getUrl('get-supplier-authorizations-list'),
            },
            api,
            extraOptions
          );
        }
      ),
    }),
    getUniqueAffiliateProductsList: query({
      query: params => ({
        params,
        url: getUrl('get-unique-affiliate-products-list'),
      }),
    }),
    getUniqueAffiliateSuppliersList: query({
      query: params => ({
        params,
        url: getUrl('get-unique-affiliate-suppliers-list'),
      }),
    }),
    toggleSupplierAuthorization: mutation({
      /**
       * Uses createCacheUpdateHandler to update the cache without triggering a full data refetch.
       * This provides instant UI feedback when toggling supplier authorization status.
       *
       * Parameters:
       * 1. API instance (loadingManagementApi)
       * 2. Query endpoint name to update ('getSupplierAuthorizationsList')
       * 3. Update function: receives (draft, mutationArgs) and modifies the cached data
       * 4. Options: { invalidateOnError: [...tags] } - tags to invalidate if mutation fails
       *
       * The helper automatically:
       * - Waits for mutation success (pessimistic update)
       * - Finds all cached queries for the endpoint (handles pagination/sorting)
       * - Updates each cache with the new data
       * - Falls back to tag invalidation on error
       */
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getSupplierAuthorizationsList',
        (draft, { body, isActive }) => {
          const rowToUpdate = draft.supplierAuthorizations?.find(
            auth => auth.productAuthorizationId === body.productAuthorizationId
          );
          if (rowToUpdate) {
            rowToUpdate.active = !isActive;
          }
        },
        { invalidateOnError: ['SupplierAuthorizationList'] }
      ),
      query: ({ body, isActive }) => ({
        body,
        method: 'POST',
        url: getUrl(`${isActive ? 'deactivate' : 'activate-supplier'}-authorization`),
      }),
    }),
    updateTwoPartyExchangeStatus: mutation({
      invalidatesTags: ['SupplierAuthorizationList'],
      query: ({ productAuthorizationId, isActive }) => ({
        body: { productAuthorizationId },
        method: 'POST',
        url: getUrl(`${isActive ? 'deactivate-exchange' : 'activate-exchange'}`),
      }),
    }),
  }),
});

export const {
  useGetFilterLocationsListQuery,
  useGetUniqueAffiliateProductsListQuery,
  useGetUniqueAffiliateSuppliersListQuery,
  useLazyGetExportListQuery,
  useGetSupplierAuthorizationsListQuery,
  useUpdateTwoPartyExchangeStatusMutation,
  useBulkActivateSupplierAuthorizationsMutation,
  useBulkDeactivateSupplierAuthorizationsMutation,
  useToggleSupplierAuthorizationMutation,
} = supplierAuthorizationsEndpoints;
